import React, { forwardRef } from "react";
import { string, func, oneOf, bool } from "prop-types";
import cx from "classnames";
import { pascal } from "case";
// eslint-disable-next-line import/no-cycle
import { Link } from "../Link";
import { Icon } from "../../components/Icons";
import * as styles from "./Button.module.scss";
import { FULL_IMAGE_VARIANT } from "../../const/const";

const Button = forwardRef(
	(
		{
			id,
			className,
			to,
			onClick,
			onMouseEnter,
			target,
			showExternalIcon,
			icon,
			iconPlacement,
			type,
			noStyle,
			defaultStyle,
			size,
			outline,
			theme,
			label,
			disabled,
			full,
			variant,
			ariaControls,
			ariaExpanded,
		},
		ref
	) => {
		const commonStyle = cx({
			[styles[size]]: size && styles[size],
			[styles.empty]: !label,
			[styles.disabled]: disabled,
			[styles.hasLabel]: label,
			[styles[`icon${pascal(iconPlacement)}`]]:
				(icon && iconPlacement) ||
				(iconPlacement === "right" && styles[`icon${pascal(iconPlacement)}`]),
			[className]: className,
		});
		const classes = cx({
			[styles.btn]: defaultStyle,
			[commonStyle]: commonStyle,
			[styles.outline]: outline,
			[styles[theme]]: theme && styles[theme],
			[styles.full]: full,
			[className]: className,
		});

		const noStyleClasses = cx({
			[commonStyle]: commonStyle,
		});

		// Added check for empty onClick handlers
		if (to && !(onClick && onClick.length > 0)) {
			let mainstyle;
			switch (true) {
				case noStyle:
					mainstyle = noStyleClasses;
					break;
				default:
					if (variant && variant === FULL_IMAGE_VARIANT) {
						mainstyle = styles.fullImage;
					} else {
						mainstyle = classes;
					}
					break;
			}

			return (
				<Link to={to} target={target} className={mainstyle} showExternalIcon={showExternalIcon}>
					{icon && iconPlacement === "left" && <Icon name={icon} className={styles.btnIcon} />}
					{label}
					{iconPlacement === "right" && <Icon name="arrow-thick" className={styles.btnIcon} />}
				</Link>
			);
		}

		return (
			<button
				// eslint-disable-next-line react/button-has-type
				type={type}
				aria-label={label || "Aria Label"}
				className={noStyle ? noStyleClasses : classes}
				disabled={disabled}
				onMouseEnter={onMouseEnter}
				onClick={onClick}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				ref={ref}
				id={id}>
				{iconPlacement === "left" && <Icon name={icon} className={styles.btnIcon} />}
				{label}
				{iconPlacement === "right" && <Icon name="arrow-thick" className={styles.btnIcon} />}
			</button>
		);
	}
);

Button.propTypes = {
	showExternalIcon: bool,
	icon: string,
	iconPlacement: oneOf(["right", "left"]),
	className: string,
	to: string,
	label: string,
	target: string,
	onClick: func,
	onMouseEnter: func,
	type: oneOf(["button", "submit", "reset"]),
	defaultStyle: bool,
	outline: bool,
	theme: oneOf([false, "primary", "primary-invert"]),
	size: oneOf(["sm", "md", "lg", "xl", "auto"]),
	disabled: bool,
	full: bool,
	noStyle: bool,
	variant: string,
	id: string,
	ariaControls: string,
	ariaExpanded: bool,
};

Button.defaultProps = {
	showExternalIcon: false,
	icon: undefined,
	iconPlacement: "left",
	className: undefined,
	label: "",
	to: undefined,
	onClick: undefined,
	onMouseEnter: undefined,
	target: "_self",
	type: "button",
	defaultStyle: true,
	outline: false,
	theme: "primary",
	size: "md",
	disabled: false,
	full: false,
	noStyle: false,
	variant: "",
	id: undefined,
	ariaControls: undefined,
	ariaExpanded: undefined,
};

export default Button;
export { Button };
